import {
  Calendar,
  Clock,
  MessageSquare,
  Phone,
  Scissors,
  Sparkles,
} from 'lucide-react';

import { IndustryPage, type IndustryPageConfig } from '@/components/marketing/industry-page';

const config: IndustryPageConfig = {
  seoTitle: 'AI Receptionist for Salons & Spas — Never Miss a Booking Call',
  seoDescription:
    'Stop missing booking calls when your team is with clients. AI receptionist for salons and spas that captures appointment requests, answers service FAQs, and works 24/7.',
  path: '/industries/salons',
  badge: 'Salons & Spas',
  headline: 'Booking calls answered while your team stays with clients.',
  subheadline:
    'Your AI receptionist captures appointment requests, answers questions about services and pricing, and lets your team focus fully on the client in their chair.',
  pains: [
    'Your team can\'t answer the phone mid-color or during a treatment without disrupting the client experience.',
    'After-hours booking requests sit in voicemail and clients book elsewhere or forget to call back.',
    'Time spent on the phone answering "how much is a balayage" pulls staff away from revenue-generating work.',
  ],
  features: [
    {
      icon: Phone,
      title: 'Always-on call answering',
      desc: 'Every call answered, even when every stylist is mid-appointment. No more ringing out.',
    },
    {
      icon: Calendar,
      title: 'Appointment request capture',
      desc: 'Name, service requested, stylist preference, and preferred date/time — captured and logged for your team to confirm.',
    },
    {
      icon: Scissors,
      title: 'Service & pricing Q&A',
      desc: 'Balayage, keratin treatments, massage types, prices — answered from your service menu. Accurate, consistent, every time.',
    },
    {
      icon: Sparkles,
      title: 'Personalised brand voice',
      desc: 'Configure the greeting, name, and tone to match your salon\'s vibe — whether that\'s luxe and quiet or energetic and fun.',
    },
    {
      icon: MessageSquare,
      title: 'Website chat on the same brain',
      desc: 'The same knowledge base powers your phone and website chat — train once, answer everywhere.',
    },
    {
      icon: Clock,
      title: 'After-hours booking capture',
      desc: 'Clients book at 10pm on Sunday. Your AI captures the request; your team confirms Monday morning.',
    },
  ],
  faqs: [
    {
      q: 'Can it take bookings directly into my salon software?',
      a: 'The agent captures booking requests and stores them in your dashboard. Direct booking software integration is on the roadmap.',
    },
    {
      q: 'Can I configure it to mention current promotions?',
      a: 'Yes — add your promotions and seasonal offers to your knowledge base and the agent mentions them when relevant.',
    },
    {
      q: 'What if a client wants a specific stylist who is fully booked?',
      a: 'Configure the agent to capture waitlist requests and mention alternative stylists or dates.',
    },
    {
      q: 'Does it handle calls in languages other than English?',
      a: 'The agent currently handles English. Multilingual support is on the roadmap.',
    },
    {
      q: 'Can I use it for a day spa as well as a hair salon?',
      a: 'Yes — it works for any appointment-based service business. Just configure your treatment menu in the knowledge base.',
    },
  ],
  ctaHeadline: 'Let your team focus on the client in their chair.',
  ctaSub: 'AI receptionist for salons, live in under 10 minutes.',
};

export function SalonsIndustryPage(): JSX.Element {
  return <IndustryPage config={config} />;
}
